<template>
  <div class="change-password-page flex-grow-1 d-flex flex-column">
    <div class="a-body flex-grow-1" style="padding: 10px 30px">
      <div class="info-item">
        We strongly encourage all our customers to enable the two-factor
        authentication (2FA) option for accessing their account. 2FA is an extra
        layer of security that requires not only a password but also something
        that only the user has on them. When 2FA is enabled, you must provide a
        username, password and an authentication code from a 2FA device.
        You may use any app that supports the Time-Based One-Time Password (TOTP)
        standard on your smartphone or tablet, such as Google Authenticator
        (<a href="https://apps.apple.com/sg/app/google-authenticator/id388497605">iOS</a>,
        <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">Android</a>).
      </div>
      <br>
      <div>
        <span
          class="color-prLgt mr-2"
          :class="{ 'color-ace': userDetail.enable_2fa }"
        >
          2FA is {{ !userDetail.enable_2fa ? "disabled" : "enabled" }} <span v-if="userDetail.username && userDetail.username != this.$store.state.user.data.username">for {{userDetail.username}}</span>
        </span>
        <a-switch v-model="userDetail.enable_2fa" @change="onChange2FA" />
      </div>        
    </div>
    <!-- Modal Activate 2FA -->
    <a-modal
            v-model="isShow2FA"
            class="active-2fa-modal"
            :footer="null"
            :title="
        !userDetail.enable_2fa
          ? 'Activate Two-Factor Authentication (2FA)'
          : 'Deactivate Two-Factor Authentication (2FA)'
      "
            onOk="onSubmit2FA"
    >
      <a-form :form="active2FAForm">
        <a-row v-if="!userDetail.enable_2fa">
          <a-col :span="8">
            <div class="qr-code">
              <div class="qr-code--img">
                <img :src="secret2FA.qr"/>
              </div>
              <div class="qr-code--text mt-1">
                Manual Authentication Key
              </div>
              <div class="qr-code--text fw-b">
                {{ secret2FA.secret }}
              </div>
            </div>
          </a-col>
          <a-col :span="16" class="pl-10-px">

            <div class="fw-b mb-2 color-pr">
              SETUP
            </div>
            <ol>
              <li>Open the 2FA app on your mobile device and add a new entry.</li>
              <li>Scan the QR code when prompted by the app.</li>
              <li>Type in the 6-digit code (shown in the app) into the box below.</li>
            </ol>
            <div class="fw-b mb-2 color-pr">
              NOTE
            </div>
            <p>
              If you are having issues with our system rejecting your authentication code,
              please ensure that your mobile device is set to the correct time, and you are using the latest
              code shown in the 2FA app. A new code will be generated every 30 seconds.
            </p>
            <p>
              Once activated, you will need the 6-digit code generated by the 2FA app to login.
              If you are unable to login, please contact support.
            </p>
          </a-col>
        </a-row>
        <a-form-item
                v-if="userDetail.enable_2fa"
                label="Password"
                class="password"
        >
          <a-input-password
                  v-decorator="[
              'password',
              {
                rules: [
                  {
                    min: 8,
                    message: 'Password needs to be at least 8 characters!',
                  },
                  { required: true, message: 'Please input Password!' },
                ],
              },
            ]"
                  type="password"
                  placeholder="Password"
          />
        </a-form-item>
        <a-form-item class="mt-2" label="OTP">
          <a-input
                  v-decorator="[
              'otp',
              {
                rules: [{ required: true, message: 'Please input OTP' }],
              },
            ]"
                  placeholder="Enter 6-digit Authentication Key"
          />
        </a-form-item>
        <small>
          Please enter the verification code from your app within 30 seconds
        </small>
        <div class="a-footer">
          <a-button key="back" class="mr-3" @click="onClose2FAModal">
            Cancel
          </a-button>
          <a-button
            key="submit"
            :disabled="!active2FAForm.getFieldValue('otp')"
            type="primary"
            html-type="submit"
            @click="onSubmit2FA"
          >
            Save
          </a-button>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
  import {
    debounceTimeExecute,
    flatMapErrMessagesValidateForm,
  } from "@shared/helpers/utils";
  import AccountService from '../services/account.service';
  export default {
    data() {
      return {
        userDetail: {},
        isShow2FA: false,
        secret2FA: {},
      };
    },
    beforeCreate() {
      this.active2FAForm = this.$form.createForm(this, {
        name: "normal_activate2fa",
      });
    },
    mounted() {
      this.initData();
    },
    methods: {
      async initData() {
        const userDetail = await AccountService.getUser();
        this.userDetail = {
          ...userDetail,
          enable_2fa: !!userDetail.enable_2fa,
        };
      },
      onSubmit2FA(e) {
        e.preventDefault();
        this.active2FAForm.validateFields(async (err, values) => {
          if (!err) {
            if (this.userDetail.enable_2fa) {
              const bodyParams = {
                otp: values.otp,
                password: values.password,
              };
              try {
                await AccountService.deactivate2FA(bodyParams);
                this.userDetail.enable_2fa = false;
                this.onClose2FAModal();
                this.$showSuccessMessage("Deactivated 2FA!");
              } catch (error) {
                const messages =
                        error && error.messages && error.messages.length
                                ? error.messages
                                : ["Failed to deactive 2FA"];
                this.$showMultiErrorMessage(messages);
              }
            } else {
              const bodyParams = {
                otp: values.otp,
                secret: this.secret2FA.secret,
              };
              try {
                await AccountService.activate2FA(bodyParams);
                this.userDetail.enable_2fa = true;
                this.onClose2FAModal();
                this.$showSuccessMessage("Activated 2FA!");
              } catch (error) {
                const messages =
                        error && error.messages && error.messages.length
                                ? error.messages
                                : ["Failed to active 2FA"];
                this.$showMultiErrorMessage(messages);
              }
            }
          } else {
            debounceTimeExecute(
                    flatMapErrMessagesValidateForm(err),
                    this.$showErrorMessage
            );
          }
        });
      },
      async getUser() {
        const userDetail = await AccountService.getUserInRole();
        this.userDetail = {
          ...userDetail,
          enable_2fa: !!userDetail.enable_2fa,
        };
      },
      async getTimezoneList() {
        this.timezoneList = await DataService.getTimezones();
      },
      async get2FASecret() {
        this.secret2FA = await AccountService.get2FASecret();
      },
      onChange2FA(event) {
        this.isShow2FA = true;
        if (event) {
          this.get2FASecret();
          this.userDetail.enable_2fa = false;
        } else {
          this.userDetail.enable_2fa = true;
        }
      },
      onClose2FAModal() {
        this.isShow2FA = false;
        this.active2FAForm.resetFields();
      },
      filterTimezone(input, option) {
        return (
                option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
        );
      },
      active2FAInvalid() {
        return !this.active2FAForm.getFieldValue("otp");
      },
    }
  };
</script>

<style lang="scss" scoped>
  .change-password-page {
    .ant-form label::before {
      display: none;
    }
    .a-footer .ant-btn {
      min-width: 120px;
    }
  }
</style>
